/**
 * deepLink.js — Issue #307. Maps Discord deep-link URLs onto the v2 SPA.
 *
 * Discord embeds (see src/discord/formatter.js) link users to legacy
 * paths like /price?item=… and /sales?tag=… . The v2 SPA serves on /v2
 * but the SPA fallback (src/web/server.js) hands those bare paths the
 * v2 index.html, so the page loads — we just have to translate the URL
 * into a window-manager action once we're booted.
 *
 * Also covers /v2/?item=… and /v2/?tag=… so the SPA's own URL shape can
 * carry deep-links going forward without breaking the legacy ones.
 *
 * Pure functions — no DOM access in parseDeepLink so it's trivially
 * testable from Node.
 */

const PATH_PRICE = /^\/(?:v2\/?)?price\/?$/;
const PATH_SALES = /^\/(?:v2\/?)?sales\/?$/;
const PATH_V2    = /^\/v2\/?$/;

/**
 * Parse a same-origin pathname + search into a deep-link action, or
 * null if nothing matches.
 *
 * @param {string} pathname  e.g. '/price' or '/sales'
 * @param {string} search    e.g. '?item=Dreugh%20Wax'
 * @returns {{type:'item',name:string}|{type:'user',tag:string}|null}
 */
export function parseDeepLink(pathname, search) {
  if (typeof pathname !== 'string') return null;
  const params = new URLSearchParams(search || '');
  const item = params.get('item');
  const tag  = params.get('tag');

  if (PATH_PRICE.test(pathname) && item) {
    return { type: 'item', name: item };
  }
  if (PATH_SALES.test(pathname) && tag) {
    return { type: 'user', tag };
  }
  // /v2/ with explicit deep-link query — preferred shape going forward.
  if (PATH_V2.test(pathname)) {
    if (item) return { type: 'item', name: item };
    if (tag)  return { type: 'user', tag };
  }
  return null;
}

/**
 * Fire the appropriate window-level CustomEvent so the corresponding
 * modal (mounted at app root) can pick it up. Returns true if a known
 * action was dispatched.
 */
export function dispatchDeepLink(link) {
  if (!link || typeof window === 'undefined') return false;
  if (link.type === 'item' && link.name) {
    window.dispatchEvent(new CustomEvent('v2-item-pick', { detail: { name: link.name } }));
    return true;
  }
  if (link.type === 'user' && link.tag) {
    window.dispatchEvent(new CustomEvent('v2-user-pick', { detail: { tag: link.tag } }));
    return true;
  }
  return false;
}

export default { parseDeepLink, dispatchDeepLink };
